import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';

interface UseApiProps {
    apiFunc: any;
    onSuccess?: any;
    onError?: any;
    enableLoading?: any;
    successMessage?: string;
    errorMessage?: string | null;
    loadingState?: any;
    showError?: boolean;
}

type ApiResponse = {
    meta?: {
        requestStatus?: string;
    };
    error?: {
        message?: string;
    };
    payload?: any
};

type Callback = () => void;

function apiHelper({
    res,
    callBack,
    showError = true,
    errorCallback,
    successMessage = null,
    enqueueSnackbar,
}: {
    res: ApiResponse;
    callBack?: Callback;
    showError?: boolean;
    errorCallback?: (error: any) => void;
    successMessage?: string | null;
    enqueueSnackbar: any;
}): void {
    if (res?.meta?.requestStatus === "rejected") {
        const errorMessage = (() => {
            try {
                const msg = res.error?.message || res?.payload?.message;
                return msg ? JSON.parse(msg) : null;
            } catch {
                return { message: res.error?.message || res?.payload?.message || "Something went wrong" };
            }
        })();

        const rawError = errorMessage?.message;
        let message = "Something went wrong";

        if (typeof rawError === "string") {
            message = rawError.replace("Error:", "").trim();
        } else if (rawError && typeof rawError === "object") {
            message = (rawError as any).message || JSON.stringify(rawError);
        } else if (rawError) {
            message = String(rawError);
        }

        if (showError && typeof enqueueSnackbar === 'function') {
            enqueueSnackbar(message, { variant: "error" });
        }

        if (errorCallback && typeof errorCallback === "function") {
            errorCallback(message);
        }
        return;
    }

    if (successMessage && typeof enqueueSnackbar === 'function') {
        enqueueSnackbar(successMessage);
    }

    if (callBack && typeof callBack === "function") {
        callBack();
    }
}

export default function useApi() {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const callApi = useCallback(async ({
        apiFunc,
        onSuccess,
        onError,
        successMessage,
        errorMessage,
        loadingState,
        enableLoading = true,
        showError = true,
    }: UseApiProps) => {
        if (enableLoading) setLoading(true);
        loadingState?.(true);
        let response;
        try {
            response = await apiFunc();
        } catch (err: any) {
            response = {
                meta: { requestStatus: "rejected" },
                error: { message: err?.message || "Something went wrong" }
            };
        }
        if (enableLoading) setLoading(false);

        loadingState?.(false);
        apiHelper(
            {
                res: response,
                callBack: () => {
                    onSuccess?.(response);
                },
                errorCallback: (err) => {
                    onError?.(response);
                    setError(errorMessage || err);
                },
                successMessage: successMessage,
                showError,
                enqueueSnackbar,
            }
        )

        return { response, error }

    }, [enqueueSnackbar])

    return { callApi, loading, error };
}
